import { parseReactGoogleAddress } from 'src/lib/address.utils'
import { PLACES_INCLUDED_REGION_CODES } from 'src/lib/placesRegions'

export type ZipLookupResult = {
  street: string
  city: string
  state: string
  country: string
  zipCode: string
}

const US_OR_MX_POSTAL = /^\d{5}$/
// ANA NAN, space optional. Letter-class is loose on purpose: Google rejects
// unknowns, and we only need "complete enough to look up" vs "still typing".
const CANADIAN_POSTAL = /^[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d$/

export const normalizePostalCode = (value: string): string =>
  value.replace(/[\s-]/g, '').toUpperCase()

export const isCompletePostalCode = (value: string): boolean => {
  const zip = value.trim()
  return US_OR_MX_POSTAL.test(zip) || CANADIAN_POSTAL.test(zip)
}

// ZIP+4 is accepted on submission but excluded from isCompletePostalCode,
// since Google resolves the 5-digit prefix and the suffix adds nothing.
const US_ZIP_PLUS_FOUR = /^\d{5}-\d{4}$/

export const isValidPostalCode = (value: string): boolean => {
  const zip = value.trim()
  return isCompletePostalCode(zip) || US_ZIP_PLUS_FOUR.test(zip)
}

// Resolves a zip code to its city/state/country through the same Places REST
// API (and key) that PlacesAutocomplete already uses, so no additional Google
// API needs to be enabled. Returns null when the input doesn't match a known
// postal code.
export async function lookupZipCode(
  zipCode: string
): Promise<ZipLookupResult | null> {
  const API_KEY = process.env.GOOGLE_MAPS_API_KEY ?? ''

  const autocompleteResponse = await fetch(
    'https://places.googleapis.com/v1/places:autocomplete',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Goog-Api-Key': API_KEY,
      },
      body: JSON.stringify({
        input: zipCode,
        includedPrimaryTypes: ['postal_code'],
        includedRegionCodes: [...PLACES_INCLUDED_REGION_CODES],
      }),
    }
  )
  if (!autocompleteResponse.ok) {
    throw new Error(`HTTP error! status: ${autocompleteResponse.status}`)
  }

  const autocompleteData = await autocompleteResponse.json()
  const placeId = autocompleteData?.suggestions?.[0]?.placePrediction?.placeId
  if (!placeId) {
    return null
  }

  const detailsResponse = await fetch(
    `https://places.googleapis.com/v1/places/${placeId}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-Goog-Api-Key': API_KEY,
        'X-Goog-FieldMask': 'addressComponents',
      },
    }
  )
  if (!detailsResponse.ok) {
    throw new Error(`HTTP error! status: ${detailsResponse.status}`)
  }

  const parsed = parseReactGoogleAddress(await detailsResponse.json())
  if (!parsed.city && !parsed.state) {
    return null
  }
  return parsed
}
